import { CheckboxTree, type TreeNode } from "@spz/ui/components/checkbox-tree";
import Loader from "@spz/ui/components/loader";
import { Section } from "@spz/ui/components/section";
import { TextField } from "@spz/ui/components/textfield";
import { Typography } from "@spz/ui/components/typography";
import { useMemo, useState } from "react";
import type { FieldError, Merge } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/utils";
import { InlineFeedback, InlineFeedbackGroup } from "./inline-feedback";

type SearchableCheckboxTreeFieldProps<
  E extends Merge<FieldError, (FieldError | undefined)[]> = FieldError,
> = {
  label: string;
  description?: string;
  searchPlaceholder?: string;
  emptyList: string;
  loading?: boolean;
  options: TreeNode[];
  value?: string[];
  helperText?: string;
  error?: E;
  selectAllText?: string;
  onChange: ({
    selectedNode,
    all,
    selectedIds,
    toggleNodeIds,
  }: {
    selectedNode: TreeNode & { isChecked: boolean };
    all: boolean;
    selectedIds: string[];
    toggleNodeIds: string[];
  }) => void;
};

export const SearchableCheckboxTreeField = ({
  label,
  description = "",
  searchPlaceholder,
  emptyList,
  options,
  value = [],
  loading = false,
  helperText,
  error,
  selectAllText = "select_all",
  onChange,
}: SearchableCheckboxTreeFieldProps) => {
  const { t } = useTranslation();

  const [searchInput, setSearchInput] = useState("");

  const flattenedTree = useMemo(() => {
    const mappedChildren = options.map((option) => ({
      ...option,
      defaultChecked: value.includes(option.id),
    }));
    const checkedCount = mappedChildren.filter(
      (child) => child.defaultChecked,
    ).length;

    return {
      id: "root",
      label: `${t(selectAllText, { count: checkedCount })}`,
      className: "font-bold",
      children: mappedChildren,
    };
  }, [options, t, value, selectAllText]);
  const filteredTree = useMemo(
    () => ({
      ...flattenedTree,
      children: flattenedTree.children.filter((child) =>
        child.label.toLowerCase().includes(searchInput.toLowerCase()),
      ),
    }),
    [flattenedTree, searchInput],
  );

  const handleCheckedChange = ({
    node,
    isChecked,
    checkedNodeIds,
  }: { node: TreeNode; isChecked: boolean; checkedNodeIds: string[] }) => {
    const visibleIds = filteredTree.children.map((child) => child.id);
    const newSelectedIds = computeUpdatedSelection({
      currentSelection: value,
      visibleIds,
      nodeIdsCheckedNow: checkedNodeIds,
    });

    onChange({
      all: newSelectedIds.length === options.length,
      selectedIds: newSelectedIds,
      selectedNode: {
        ...node,
        isChecked,
      },
      toggleNodeIds: visibleIds,
    });
  };

  return (
    <Section title={label} description={description} className="gap-4">
      <TextField
        leftIcon="search"
        rightIcon="x"
        placeholder={searchPlaceholder}
        className="w-full"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        rightIconClick={() => setSearchInput("")}
      />

      <div
        className={cn(
          "flex flex-col h-[190px] overflow-y-auto border rounded px-2 py-1",
          error ? "border-red-500" : "border-gray-200",
          "pink-scroll",
        )}
      >
        {loading ? (
          <Loader size="size-6" />
        ) : filteredTree.children.length > 0 ? (
          <CheckboxTree
            tree={filteredTree}
            onCheckedChange={handleCheckedChange}
          />
        ) : (
          <Typography
            trans={emptyList}
            className="text-gray-500 text-center mt-2"
          />
        )}
      </div>

      {(error?.message || helperText) && (
        <InlineFeedbackGroup>
          {error?.message && (
            <InlineFeedback variant="critical" message={error.message} nested />
          )}
          {helperText && (
            <InlineFeedback variant="info" message={helperText} nested />
          )}
        </InlineFeedbackGroup>
      )}
    </Section>
  );
};

export function computeUpdatedSelection({
  currentSelection,
  visibleIds,
  nodeIdsCheckedNow,
}: {
  currentSelection: string[];
  visibleIds: string[];
  nodeIdsCheckedNow: string[];
}): string[] {
  const previouslyCheckedInVisible = currentSelection.filter((id) =>
    visibleIds.includes(id),
  );
  const currentlyCheckedInVisible = nodeIdsCheckedNow.filter(
    (id) => id !== "root",
  );

  const toAdd = currentlyCheckedInVisible.filter(
    (id) => !previouslyCheckedInVisible.includes(id),
  );
  const toRemove = previouslyCheckedInVisible.filter(
    (id) => !currentlyCheckedInVisible.includes(id),
  );

  // New global selection
  return currentSelection
    .filter((id) => !toRemove.includes(id))
    .concat(toAdd)
    .filter((v, i, a) => a.indexOf(v) === i);
}
